import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../utils/AuthContext';
import {
  Box,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Select,
  Input,
  Flex,
} from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addMonths, subMonths } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import * as XLSX from 'xlsx';

registerLocale('en-GB', enGB);

const ViewAssignedPaperSets = () => {
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paperSets, setPaperSets] = useState([]);
  const [examDate, setExamDate] = useState('');
  const { auth } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [selectedPaperSetId, setSelectedPaperSetId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Exam/ExamAssignments`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        });
        const assignmentsData = response.data.$values || response.data;
        setAssignments(assignmentsData);
        setFilteredAssignments(assignmentsData);
      } catch (error) {
        setError('Error fetching assignments');
        console.error('Error fetching assignments:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPaperSets = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        });
        const paperSetsData = response.data.$values || response.data;
        setPaperSets(paperSetsData);
      } catch (error) {
        setError('Error fetching paper sets');
        console.error('Error fetching paper sets:', error);
      }
    };

    fetchAssignments();
    fetchPaperSets();
  }, [auth.token]);

  useEffect(() => {
    filterAssignmentsByMonth();
  }, [selectedMonth, assignments]);

  const exportToExcel = () => {
    const formattedData = filteredAssignments.map((assignment) => ({
      'Emp Name': assignment.empName,
      User: assignment.userName,
      City: assignment.city,
      'Paper Set': assignment.paperSetTitle,
      'Exam Date': new Date(assignment.examDate).toLocaleDateString(),
      Completed: assignment.isCompleted ? 'Yes' : 'No',
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Assigned Paper Sets');
    XLSX.writeFile(workbook, `Assigned_Paper_Sets_${selectedMonth.toISOString().slice(0, 7)}.xlsx`);
  };

  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setSelectedPaperSetId(assignment.paperSetId);
    setExamDate(new Date(assignment.examDate).toISOString().split('T')[0]);
    onOpen();
  };

  const handleSave = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/Exam/ExamAssignments/${editingAssignment.examAssignmentId}`,
        { ...editingAssignment, paperSetId: selectedPaperSetId, examDate },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );
      setAssignments(assignments.map((assignment) =>
        assignment.examAssignmentId === editingAssignment.examAssignmentId
          ? { ...assignment, paperSetId: selectedPaperSetId, examDate }
          : assignment
      ));
      onClose();
    } catch (error) {
      console.error('Error updating assignment:', error.response ? error.response.data : error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/Exam/ExamAssignments/${id}`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        });
        setAssignments(assignments.filter((assignment) => assignment.examAssignmentId !== id));
      } catch (error) {
        console.error('Error deleting assignment:', error.response ? error.response.data : error.message);
      }
    }
  };

  const filterAssignmentsByMonth = () => {
    const filtered = assignments.filter((assignment) => {
      const assignmentMonth = new Date(assignment.examDate).toISOString().slice(0, 7);
      return assignmentMonth === selectedMonth.toISOString().slice(0, 7);
    });
    setFilteredAssignments(filtered);
  };

  const columnDefs = [
    { headerName: 'Emp Name', field: 'empName', flex: 1, filter: true },
    { headerName: 'User', field: 'userName', flex: 1, filter: true },
    { headerName: 'City', field: 'city', flex: 1, filter: true },
    { headerName: 'Paper Set', field: 'paperSetTitle', flex: 1, filter: true },
    { headerName: 'Exam Date', field: 'examDate', valueFormatter: params => new Date(params.value).toLocaleDateString(), flex: 1, filter: true },
    { headerName: 'Completed', field: 'isCompleted', cellRenderer: params => params.value ? 'Yes' : 'No', flex: 1 },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params) => (
        <div>
          <Button size="sm" colorScheme="blue" onClick={() => handleEdit(params.data)}>Edit</Button>
          <Button size="sm" colorScheme="red" ml={2} onClick={() => handleDelete(params.data.examAssignmentId)}>Delete</Button>
        </div>
      ),
      flex: 1,
    },
  ];

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex justify-center items-center h-full">
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8}>
      <Heading mb={6}>View Assigned Paper Sets</Heading>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <DatePicker
          selected={selectedMonth}
          onChange={(date) => setSelectedMonth(date)}
          dateFormat="MMM yyyy"
          showMonthYearPicker
          locale="en-GB"
        />
        <Button colorScheme="blue" onClick={exportToExcel}>
          Export to Excel
        </Button>
      </Flex>
      <Box className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
        <AgGridReact
          rowData={filteredAssignments}
          columnDefs={columnDefs}
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
        />
      </Box>

      {editingAssignment && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Assignment</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Select
                placeholder="Select Paper Set"
                value={selectedPaperSetId}
                onChange={(e) => setSelectedPaperSetId(e.target.value)}
              >
                {paperSets.map((paperSet) => (
                  <option key={paperSet.paperSetId} value={paperSet.paperSetId}>
                    {paperSet.title}
                  </option>
                ))}
              </Select>
              <Input
                type="date"
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                mt={4}
              />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleSave}>
                Save
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default ViewAssignedPaperSets;
